<template>
  <div class="product">
    <div class="container">
      <div class="product__wrapper" v-if="product">
        <div class="product__left">
          <h1 class="product__name">{{ product.name }}</h1>
          <div class="product__price">
            <p style="color: #3b3b3b">€</p>
            <p>{{ product.price }}</p>
          </div>
          <div v-if="product.description" class="product__description">
            <h6>{{ t('product.description') }}:</h6>
            <div v-html="product.description" />
          </div>
          <div class="product__buttons">
            <div @click="overwriteCart">
              <img src="@icons/cartIcon.svg" alt="cart">
              <p>{{ isProductInCart ? t('buttons.remove') : t('buttons.cart') }}</p>
            </div>
            <div @click="overwriteWishlist">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" viewBox="0 0 18 17" fill="none">
                <path d="M9.40992 16.3264C13.7219 16.3264 17.2174 12.8309 17.2174 8.51892C17.2174 4.20696 13.7219 0.711426 9.40992 0.711426C5.09795 0.711426 1.60242 4.20696 1.60242 8.51892C1.60242 12.8309 5.09795 16.3264 9.40992 16.3264Z" stroke="#DD6196" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M9.40724 6.22969C8.62675 5.31723 7.32521 5.07177 6.34731 5.90732C5.3694 6.74287 5.23172 8.13986 5.99968 9.12807C6.49156 9.76102 7.75128 10.9348 8.59344 11.6969C8.8733 11.9501 9.01322 12.0767 9.18083 12.1275C9.32492 12.1712 9.48957 12.1712 9.63365 12.1275C9.80126 12.0767 9.94119 11.9501 10.2211 11.6969C11.0632 10.9348 12.3229 9.76102 12.8148 9.12807C13.5828 8.13986 13.4619 6.73408 12.4672 5.90732C11.4725 5.08056 10.1877 5.31723 9.40724 6.22969Z" stroke="#DD6196" :fill="isProductInWishlist ? 'red' : 'none'" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <p>{{ t('buttons.wishlist') }}</p>
            </div>
          </div>
        </div>
        <img class="product__image" :src="product.images.edges[0]?.node.image" :alt="product.name">
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {Products} from "@/core/api/products.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useCartStore} from "@/stores/cart.js";
import {useAuthStore} from "@/stores/auth.js";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";
import {Wishlist} from "@/core/api/wishlist.js";

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

const productSlug = computed(() => {
  return route.params.productSlug
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === product?.value?.uuid)
})
const isProductInWishlist = computed(() => {
  const el = wishlistStore.wishlist.products?.edges.find(
      (el) => el?.node?.uuid === product.value.uuid
  );

  return !!el;
});

const isLoading = ref(false)
const product = ref(null)

const getProductBySlug = async () => {
  isLoading.value = true

  const res = await Products.getProductBySlug(productSlug.value)
  product.value = res.data.products.edges[0].node

  isLoading.value = false
}

const overwriteCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);

      cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, product.value.uuid);
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      if (res.errors) {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      } else {
        ElNotification({
          message: t('popup.addToCart', { product: product.value.name }),
          type: "success"
        });
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
      }
    })
  }
}

const overwriteWishlist = async () => {
  if (user.value) {
    if (isProductInWishlist.value) {
      const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, product.value.uuid)
      wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
    } else {
      const res = await Wishlist.overwriteWishlist('addWishlistProduct', accessToken.value, wishlistUuid.value, product.value.uuid)
      wishlistStore.wishlist = res.data.addWishlistProduct.wishlist

      if (res.data.addWishlistProduct) {
        ElNotification({
          message: t('popup.addToWishlist', { product: product.value.name }),
          type: "success"
        });
      } else {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      }
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
      }
    })
  }
}

onMounted(async () => {
  await getProductBySlug()
})
</script>

<style lang="scss" scoped>
.product {
  padding-top: 150px;

  &__wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 100px;
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  &__name {
    color: #DD6196;
    font-size: 48px;
    font-weight: 700;
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 10px;

    & img {
      width: 36px;
    }

    & p {
      color: #000000;
      font-size: 32px;
      font-weight: 700;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    gap: 5px;

    & h6 {
      color: #000000;
      font-size: 26px;
      font-weight: 600;
    }

    & div {
      text-align: justify;
      color: #000000;
      font-size: 15px;
      font-weight: 500;
    }
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: 20px;

    & div {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 10px;

      @include hover {
        & p {
          color: #DD6196;
        }
      }

      &:first-child {
        padding-right: 20px;
        border-right: 1px solid #D9D9D9;
      }

      & img {
        width: 36px;
      }

      & p {
        transition: 0.2s;
        color: #000000;
        font-size: 24px;
        font-weight: 700;
        line-height: 27px;
      }
    }
  }

  &__image {
    flex-shrink: 0;
    width: 300px;
    object-fit: cover;
    border-radius: 20px;
  }
}

@media (max-width: 1000px) {
  .product {
    &__name {
      font-size: 32px;
    }

    &__price {
      & p {
        font-size: 24px;
      }
    }

    &__description {
      & h6 {
        font-size: 22px;
      }

      & p {
        font-size: 18px;
      }
    }

    &__buttons {
      & div {
        & p {
          font-size: 20px;
        }
      }
    }
  }
}

@media (max-width: 750px) {
  .product {
    &__wrapper {
      flex-direction: column-reverse;
      align-items: center;
      gap: 50px;
    }
  }
}
</style>